import { cn } from "@d12labs/utils";
import { ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { TranscriptMessage, TranscriptTool } from "../api";

const ROLE_LABEL: Record<TranscriptMessage["role"], string> = {
	user: "You",
	assistant: "Assistant",
};

/** `Sep 21, 10:04` in UTC, matching `dayLabel`. Empty for a missing or bad stamp. */
export function timeLabel(value: string | null | undefined): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: "UTC",
	});
}

function TruncatedTag() {
	return (
		<span className="flex-none rounded border border-warning/30 bg-warning/10 px-1 py-px text-[10px] font-medium uppercase leading-4 tracking-[0.06em] text-warning">
			truncated
		</span>
	);
}

function Payload({ label, value }: { label: string; value: string | null }) {
	return (
		<div className="min-w-0">
			<div className="mb-1 text-eyebrow uppercase text-ink-tertiary">{label}</div>
			{value === null ? (
				<p className="text-meta text-ink-subtle">No output</p>
			) : (
				<pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface-2 px-3 py-2 font-mono text-[12px] leading-5 text-ink-muted [overflow-wrap:anywhere]">
					{value}
				</pre>
			)}
		</div>
	);
}

/**
 * One tool call, collapsed to name + summary. Input and output render as text
 * in <pre>: tool output can hold fetched web pages, so it is never parsed as
 * markdown or HTML.
 */
export function ToolRow({ tool }: { tool: TranscriptTool }) {
	return (
		<details
			data-slot="tool-row"
			className="group min-w-0 rounded-md border border-hairline bg-surface-1"
		>
			<summary className="flex min-w-0 cursor-pointer list-none items-center gap-2 px-3 py-1.5 text-body-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
				<ChevronRight
					aria-hidden
					className="size-3.5 flex-none text-ink-tertiary transition-transform group-open:rotate-90"
				/>
				<span className="flex-none font-mono text-[12px] text-foreground">{tool.name}</span>
				<span className="min-w-0 truncate text-ink-subtle">{tool.summary}</span>
				{tool.truncated ? <TruncatedTag /> : null}
			</summary>
			<div className="space-y-2 border-t border-hairline px-3 py-2">
				<Payload label="Input" value={tool.input} />
				<Payload label="Output" value={tool.output} />
			</div>
		</details>
	);
}

/**
 * A transcript turn: You / Assistant, assistant text as markdown, then its tool
 * calls. No `rehype-raw`, so HTML in a transcript stays inert text.
 */
export function TranscriptMessageItem({ message }: { message: TranscriptMessage }) {
	const time = timeLabel(message.at);
	return (
		<li data-role={message.role} className="min-w-0 space-y-2">
			<div className="flex items-baseline gap-2">
				<span
					className={cn(
						"text-body-sm font-medium",
						message.role === "user" ? "text-foreground" : "text-primary",
					)}
				>
					{ROLE_LABEL[message.role]}
				</span>
				{time ? <span className="text-meta tabular-nums text-ink-subtle">{time}</span> : null}
			</div>
			{message.text ? (
				message.role === "assistant" ? (
					<div className="min-w-0 overflow-x-auto">
						<div
							data-slot="message-text"
							className="markdown-body text-body-sm text-ink-muted [overflow-wrap:anywhere]"
						>
							<ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
						</div>
					</div>
				) : (
					<p
						data-slot="message-text"
						className="whitespace-pre-wrap break-words text-body-sm text-foreground [overflow-wrap:anywhere]"
					>
						{message.text}
					</p>
				)
			) : null}
			{message.tools.length ? (
				<div className="space-y-1.5">
					{message.tools.map((tool, i) => (
						// Tools have no id; position within the message is stable.
						// biome-ignore lint/suspicious/noArrayIndexKey: a message's tool list never reorders.
						<ToolRow key={i} tool={tool} />
					))}
				</div>
			) : null}
		</li>
	);
}
